import React, { useEffect, useMemo, useState } from "react";
import { Image, Platform, Pressable, StyleSheet } from "react-native";
import { Chip } from "react-native-paper";
import { useThemeSettings } from "../ThemeProvider";
import getGlobalStyles from "../globalStyles";

const IMPACT_TAG_IMAGES: Record<string, ReturnType<typeof require>> = {
  social: require("../assets/impact/social.png"),
  lifestyle: require("../assets/impact/daily_life.png"),
  daily_life: require("../assets/impact/daily_life.png"),
  money: require("../assets/impact/money.png"),
  health: require("../assets/impact/health.png"),
  career: require("../assets/impact/career.png"),
  big_shift: require("../assets/impact/big_shift.png"),
};

function getImpactImageSource(tag: string | undefined): ReturnType<typeof require> | null {
  if (!tag) return null;
  const normalized = tag.toLowerCase().replace(/\s+/g, "_");
  return IMPACT_TAG_IMAGES[normalized] ?? null;
}

interface ImpactTagProps {
  tag?: string;
  side?: "left" | "right";
  top?: number;
  offset?: number;
}

export const ImpactTag: React.FC<ImpactTagProps> = ({
  tag,
  side = "right",
  top = 12,
  offset = 12,
}) => {
  const { theme } = useThemeSettings();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const [showImpactTag, setShowImpactTag] = useState(false);
  const [canHover, setCanHover] = useState(true);

  useEffect(() => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      setCanHover(window.matchMedia("(hover: hover)").matches);
    }
  }, []);

  const impactSource = getImpactImageSource(tag);

  if (!impactSource) return null;

  const sideStyle = side === "left" ? { left: offset } : { right: offset };
  const chipSideStyle = side === "left" ? { left: 50 } : { right: 50 };

  return (
    <>
      <Pressable
        style={[
          styles.shareCircle,
          { top, backgroundColor: theme.colors.appBackground3 },
          sideStyle,
        ]}
        onPress={() => setShowImpactTag((s) => !s)}
        onMouseEnter={Platform.OS === "web" && canHover ? () => setShowImpactTag(true) : undefined}
        onMouseLeave={Platform.OS === "web" && canHover ? () => setShowImpactTag(false) : undefined}
      >
        <Image source={impactSource} style={styles.impactIcon} resizeMode="cover" />
      </Pressable>

      {showImpactTag && (
        <Chip
          mode="outlined"
          style={[
            gStyles.categoryChip,
            {
              position: "absolute",
              top,
              alignSelf: "flex-start",
            },
            chipSideStyle,
          ]}
          textStyle={{
            ...theme.fonts.titleSmall,
            marginHorizontal: 8,
            color: "#fff",
            textTransform: "capitalize",
            flexShrink: 0,
          }}
          ellipsizeMode="clip"
        >
          {tag}
        </Chip>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  shareCircle: {
    width: 30,
    height: 30,
    borderRadius: 20,
    borderColor: "rgba(121, 116, 126,0.6)",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    backgroundColor: "rgba(13, 13, 13, 0.60)",
    position: "absolute",
    top: 12,
  },
  impactIcon: {
    width: 20,
    height: 20,
  },
});
